/**
 * Suburbs API
 * 
 * Functions for managing suburbs data.
 */

import { supabase } from '@/integrations/supabase/client';
import type { SuburbRecord } from './types';

/**
 * Fetch suburbs with optional filters
 */
export async function fetchSuburbs(options?: {
  state?: string;
  postcode?: string;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<{ data: SuburbRecord[]; count: number }> {
  let query = (supabase as any)
    .from('suburbs')
    .select('*', { count: 'exact' });

  if (options?.state) {
    query = query.eq('state', options.state);
  }
  if (options?.postcode) {
    query = query.ilike('postcode', `${options.postcode}%`);
  }
  if (options?.search) {
    query = query.or(`locality.ilike.%${options.search}%,suburb_name.ilike.%${options.search}%`);
  }
  
  query = query.order('state').order('locality');
  
  if (options?.limit) {
    query = query.limit(options.limit);
  }
  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
  }

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching suburbs:', error);
    throw error;
  }

  return { data: data || [], count: count || 0 };
}

/**
 * Get suburb count
 */
export async function getSuburbCount(): Promise<number> {
  const { count, error } = await (supabase as any)
    .from('suburbs')
    .select('*', { count: 'exact', head: true });

  if (error) {
    console.error('Error getting suburb count:', error);
    throw error;
  }

  return count || 0;
}

/**
 * Import suburbs from CSV URL
 */
export async function importSuburbs(csvUrl: string): Promise<{ 
  success: boolean; 
  total?: number; 
  inserted?: number; 
  errors?: number;
  error?: string;
}> {
  const { data, error } = await supabase.functions.invoke('import-suburbs', {
    body: { action: 'import', csvUrl }
  });

  if (error) {
    console.error('Error importing suburbs:', error);
    return { success: false, error: error.message };
  }

  return data;
}

/**
 * Clear all suburbs
 */
export async function clearSuburbs(): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke('import-suburbs', {
    body: { action: 'clear' }
  });

  if (error) {
    console.error('Error clearing suburbs:', error);
    return { success: false, error: error.message };
  }

  return data;
}

/**
 * Get unique states for filtering
 */
export async function getUniqueStates(): Promise<string[]> {
  const { data, error } = await (supabase as any)
    .from('suburbs')
    .select('state')
    .order('state');

  if (error) {
    console.error('Error fetching states:', error);
    throw error;
  }

  const states = [...new Set((data || []).map((d: { state: string }) => d.state))];
  return states.filter(Boolean) as string[];
}
